import * as Yup from 'yup';

import type { ValueRfmInput, RecencyRfmInput } from 'src/graphql/generated';

const createBandSchema = (maximum: number) => {
  const cutoff = () =>
    Yup.number()
      .typeError('Informe um número válido')
      .required('Informe o limite da faixa')
      .integer('Use um número inteiro')
      .min(0, 'O limite não pode ser negativo')
      .max(maximum, 'O limite informado é muito alto');

  return Yup.object({
    zoneOneMax: cutoff(),
    zoneTwoMax: cutoff().moreThan(Yup.ref('zoneOneMax'), 'Use um limite maior que o anterior'),
    zoneThreeMax: cutoff().moreThan(Yup.ref('zoneTwoMax'), 'Use um limite maior que o anterior'),
    zoneFourMax: cutoff().moreThan(Yup.ref('zoneThreeMax'), 'Use um limite maior que o anterior'),
  });
};

export const RfmFormSchema = Yup.object({
  recency: createBandSchema(999999),
  frequency: createBandSchema(2147483646),
  // The existing MONEY field stores integer cents.
  value: createBandSchema(Number.MAX_SAFE_INTEGER - 1),
});

export type TRfmFormSchema = Yup.InferType<typeof RfmFormSchema>;

const emptyBands = { zoneOneMax: 0, zoneTwoMax: 0, zoneThreeMax: 0, zoneFourMax: 0 };

export const rfmFormSchemaDefaultValues: TRfmFormSchema = {
  recency: { ...emptyBands },
  frequency: { ...emptyBands },
  value: { ...emptyBands },
};

// Keep the existing mutation contract; only the four upper cutoffs drive scoring.
export function toRfmBandInput(bands: TRfmFormSchema['recency'], monetary?: false): RecencyRfmInput;
export function toRfmBandInput(bands: TRfmFormSchema['value'], monetary: true): ValueRfmInput;
export function toRfmBandInput(bands: TRfmFormSchema['recency'], monetary = false) {
  const input = {
    zoneOneMin: 0,
    zoneOneMax: bands.zoneOneMax,
    zoneTwoMin: bands.zoneOneMax + 1,
    zoneTwoMax: bands.zoneTwoMax,
    zoneThreeMin: bands.zoneTwoMax + 1,
    zoneThreeMax: bands.zoneThreeMax,
    zoneFourMin: bands.zoneThreeMax + 1,
    zoneFourMax: bands.zoneFourMax,
    zoneFiveMin: bands.zoneFourMax + 1,
    zoneFiveMax: bands.zoneFourMax + 1,
  };
  if (!monetary) return input;

  // Decimal scalars accept strings. Keep integer cents exact through JSON rather
  // than introducing a binary fraction before the backend parses the amount.
  return Object.fromEntries(
    Object.entries(input).map(([key, cents]) => {
      const digits = String(cents).padStart(3, '0');
      return [key, `${digits.slice(0, -2)}.${digits.slice(-2)}`];
    })
  );
}
